import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Camera, Download, ArrowRight, Bell, Search, ShieldCheck, Sparkles, ChevronDown } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Image - now visible on mobile too */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/7c4d59e1-81f4-4497-a148-172cdb63259f/images/d6e4cab92616e0e4b71589054e376d01dd797aac08809601fa42c7732cb3c230.png')" }}
        />
        
        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-4 py-4 md:pt-6 lg:px-12">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-10 sm:h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-2 sm:gap-4">
            <Link to="/insurance" className="relative">
              <Button variant="ghost" className="text-emerald-400 hover:text-emerald-300" data-testid="nav-insurance-btn">
                <ShieldCheck className="w-4 h-4 mr-1" />
                Insurance
              </Button>
              <Badge className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[8px] px-1 py-0">
                Coming Soon
              </Badge>
            </Link>
            <Link to="/news">
              <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="nav-news-btn">
                News
              </Button>
            </Link>
            <Link to="/police">
              <Button variant="ghost" className="text-blue-400 hover:text-blue-300" data-testid="nav-police-btn">
                Police Portal
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-900/30" data-testid="nav-login-btn">
                Login
              </Button>
            </Link>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <Link to="/login">
              <Button variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-900/30" data-testid="nav-login-btn">
                Login
              </Button>
            </Link>
          </div>
        </nav>

        {/* Mobile Hero - Animated graphic + concise text */}
        <div className="md:hidden relative z-10 px-3 pt-10 pb-6 flex flex-col items-center text-center">
          {/* Mesh grid (behind the frame, above workshop image) */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.22]" style={{
              backgroundImage: 'linear-gradient(rgba(250,204,21,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(250,204,21,0.55) 1px, transparent 1px)',
              backgroundSize: '34px 34px',
              maskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 80%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 80%)'
            }} />
          </div>

          {/* Frame wrapper - allows glow to sit behind frame's top edge */}
          <div className="relative w-full max-w-[260px] mb-6">
            {/* Yellow glow positioned at top edge - frame covers lower half, glow peeks out the top */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-3/4 h-8 rounded-full bg-primary/70 blur-xl pointer-events-none" />

            {/* Scanning frame illustration with diagonal hatch behind drill (opaque background) */}
            <div className="relative aspect-square rounded-lg border border-primary/25 overflow-hidden"
                 style={{
                   backgroundImage: 'repeating-linear-gradient(135deg, rgba(250,204,21,0.04) 0 8px, transparent 8px 16px)',
                   backgroundColor: 'hsl(var(--background))'
                 }}>
            {/* Corner brackets */}
            <span className="absolute top-0 left-0 w-7 h-7 border-t-2 border-l-2 border-primary rounded-tl-lg" />
            <span className="absolute top-0 right-0 w-7 h-7 border-t-2 border-r-2 border-primary rounded-tr-lg" />
            <span className="absolute bottom-0 left-0 w-7 h-7 border-b-2 border-l-2 border-primary rounded-bl-lg" />
            <span className="absolute bottom-0 right-0 w-7 h-7 border-b-2 border-r-2 border-primary rounded-br-lg" />

            {/* Spec labels */}
            <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.12em] text-primary/80">ID · TIO-7K2-9XQR</span>
            <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.12em] text-green-400">VERIFIED</span>
            <span className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.12em] text-primary/80">SCAN · UK REGISTRY</span>
            <span className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.12em] text-green-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              LIVE
            </span>

            {/* Scan line */}
            <div className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_20px_#facc15] scan-line-bounce" />

            {/* Tool SVG */}
            <svg viewBox="0 0 320 220" fill="none" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[210px] opacity-95">
              <defs>
                <linearGradient id="mobileToolGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#FACC15" />
                  <stop offset="1" stopColor="#EAB308" />
                </linearGradient>
              </defs>
              <rect x="60" y="60" width="160" height="80" rx="10" fill="url(#mobileToolGrad)" stroke="#0F172A" strokeWidth="2" />
              <path d="M120 140 L120 200 Q120 210 130 210 L160 210 Q170 210 170 200 L170 140 Z" fill="url(#mobileToolGrad)" stroke="#0F172A" strokeWidth="2" />
              <rect x="135" y="148" width="20" height="28" rx="3" fill="#0F172A" />
              <rect x="115" y="200" width="60" height="14" rx="3" fill="#0F172A" />
              <rect x="220" y="78" width="34" height="44" rx="3" fill="#334155" stroke="#0F172A" strokeWidth="2" />
              <circle cx="237" cy="100" r="10" fill="#0F172A" />
              <rect x="254" y="96" width="50" height="8" fill="#94A3B8" stroke="#0F172A" strokeWidth="2" />
              <polygon points="304,92 316,100 304,108" fill="#94A3B8" stroke="#0F172A" strokeWidth="2" />
              <rect x="70" y="70" width="60" height="6" fill="#0F172A" />
              <text x="76" y="76" fontFamily="ui-monospace, monospace" fontSize="6" fill="#FACC15" letterSpacing="1">TOOLIO</text>
            </svg>
            </div>
          </div>

          {/* Scan. Register. Recover. */}
          <h1 className="font-heading font-extrabold uppercase tracking-tight mb-3 leading-[1.05] whitespace-nowrap"
              style={{ fontSize: 'clamp(1.75rem, 8.5vw, 3.5rem)' }}>
            <span className="text-foreground">Scan.</span> <span className="text-primary">Register.</span> <span className="text-foreground">Recover.</span>
          </h1>

          {/* Punchy tagline */}
          <p className="text-base text-muted-foreground mb-6 max-w-sm">
            We work with Police and insurers to help recover or replace your tools and protect your livelihood. Get notified when they are found or scanned by the public. Together we can <span className="text-primary font-semibold">#StopToolTheft</span> for good.
          </p>

          {/* Buttons */}
          <div className="flex flex-col gap-3 w-full max-w-xs">
            <Link to="/register" className="w-full">
              <Button size="sm" className="btn-primary text-base px-4 py-5 w-full" data-testid="hero-register-btn-mobile">
                Register My Tools
              </Button>
            </Link>
            <Link to="/check" className="w-full">
              <Button size="sm" variant="outline" className="text-base px-4 py-5 w-full border-yellow-500 text-yellow-400 hover:bg-yellow-900/30" data-testid="hero-check-btn-mobile">
                Check a Used Tool
              </Button>
            </Link>
          </div>

          {/* Animated chevron down */}
          <div className="mt-6 animate-bounce">
            <ChevronDown className="w-20 h-8 text-primary/70 scale-x-[2]" strokeWidth={1.25} />
          </div>
        </div>

        {/* Desktop Hero Content (unchanged) */}
        <div className="relative z-10 px-6 py-12 md:py-24 lg:px-12 lg:py-32 hidden md:block">
          <div className="max-w-4xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-7xl font-extrabold uppercase tracking-tight mb-4 md:mb-6">
              Protect Your <span className="text-primary">Power Tools</span> Like Never Before
            </h1>
            
            <div className="mb-6 md:mb-8 max-w-3xl">
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground font-bold mb-3" style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}>
                TOOLIO is the smart Tool Identification & Ownership inventory system for Contractors, Tradespeople and DIYers.
              </p>
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground" style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}>
                Register your tools instantly with AI-powered scanning, track ownership and protect your investment. We work with local police and organisations to help recover your tools and stop thieves in their tracks.
              </p>
            </div>
            
            <div className="flex items-end gap-6">
              <Link to="/register">
                <Button size="lg" className="btn-primary text-lg px-8 py-6" data-testid="hero-get-started-btn">
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <div className="h-10 w-px bg-yellow-500/60 mb-1"></div>
              <div className="flex flex-col">
                <p className="text-sm text-muted-foreground mb-2" style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}>
                  Buying a pre-owned tool? Check it first
                </p>
                <Link to="/check">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-yellow-500 text-yellow-400 hover:bg-yellow-900/30" data-testid="hero-check-btn">
                    Check a Tool
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="relative z-10 bg-primary" data-testid="stats-bar">
          <div className="max-w-6xl mx-auto flex">
            <div className="flex-1 px-3 py-3 md:py-4 text-center">
              <p className="font-heading font-normal text-2xl md:text-3xl text-background leading-none mb-0">&lt;30s</p>
              <p className="font-heading font-normal text-sm md:text-base tracking-widest text-background uppercase">Per Tool</p>
            </div>
            <div className="w-px self-center h-8 md:h-10 bg-yellow-600/40" />
            <div className="flex-1 px-3 py-3 md:py-4 text-center">
              <p className="font-heading font-normal text-2xl md:text-3xl text-background leading-none mb-0">AI</p>
              <p className="font-heading font-normal text-sm md:text-base tracking-widest text-background uppercase">Scanning</p>
            </div>
            <div className="w-px self-center h-8 md:h-10 bg-yellow-600/40" />
            <div className="flex-1 px-3 py-3 md:py-4 text-center">
              <p className="font-heading font-normal text-2xl md:text-3xl text-background leading-none mb-0">Police</p>
              <p className="font-heading font-normal text-sm md:text-base tracking-widest text-background uppercase">Connected</p>
            </div>
            <div className="w-px self-center h-8 md:h-10 bg-yellow-600/40" />
            <div className="flex-1 px-3 py-3 md:py-4 text-center">
              <p className="font-heading font-normal text-2xl md:text-3xl text-background leading-none mb-0">Free</p>
              <p className="font-heading font-normal text-sm md:text-base tracking-widest text-background uppercase">To Use</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — As Easy As 1, 2, 3 */}
      <section className="relative px-6 py-20 md:py-28 lg:px-12 overflow-hidden" data-testid="how-it-works-section">
        {/* Subtle background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-16 md:mb-20">
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none">
              As Easy As <span className="text-primary">1, 2, 3</span>
            </h2>
            <p className="text-foreground text-base md:text-base font-bold max-w-md mx-auto mt-6">
              Three steps. Total protection.<br />Here&apos;s how TOOLIO keeps your tools safe.
            </p>
          </div>

          {/* Steps + Glass Card Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Steps */}
            <div className="space-y-10 md:space-y-12">
              {[
                {
                  step: "1",
                  title: "Point & Scan",
                  desc: "Open your camera and snap your tool's label. Our AI instantly reads the manufacturer, model, serial number and estimates its replacement value — no typing required.",
                  color: "text-primary",
                  borderColor: "border-primary/40"
                },
                {
                  step: "2",
                  title: "Auto-Register",
                  desc: "Your tool is added to your secure inventory with a unique TOOLIO ID. Manage your entire collection in one place — track status, value, and ownership history.",
                  color: "text-emerald-400",
                  borderColor: "border-emerald-500/40"
                },
                {
                  step: "3",
                  title: "Protected Forever",
                  desc: "If the worst happens, mark a tool stolen in one tap. Generate insurance claim reports instantly, and get notified when police or buyers scan your tool anywhere in the country.",
                  color: "text-blue-400",
                  borderColor: "border-blue-500/40"
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-5 md:gap-6 group" data-testid={`how-step-${i + 1}`}>
                  <div className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-full border-2 ${item.borderColor} flex items-center justify-center transition-colors group-hover:border-primary/60 bg-card/40`}>
                    <span className={`${item.color} font-heading font-black leading-none text-5xl md:text-6xl`}>{item.step}</span>
                  </div>
                  <div className="pt-2 md:pt-3 flex-1">
                    <h3 className="font-heading text-xl md:text-2xl font-bold uppercase mb-2">{item.title}</h3>
                    <p className="text-muted-foreground text-base leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Phone Mockup with Glass Card */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-[280px] sm:w-[300px]">
                {/* Phone frame */}
                <div className="relative rounded-[2.5rem] border-[3px] border-white/10 bg-black/80 p-3 shadow-2xl shadow-primary/10">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-20" />
                  
                  {/* Screen content */}
                  <div className="rounded-[2rem] overflow-hidden bg-gradient-to-b from-[#0d1117] to-[#161b22]">
                    {/* Tool image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.pexels.com/photos/30486882/pexels-photo-30486882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                        alt="DeWalt Power Tool" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-transparent to-transparent" />
                      {/* Scanning overlay effect */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-32 h-32 border-2 border-primary/40 rounded-lg animate-pulse" />
                      </div>
                    </div>

                    {/* Glass card */}
                    <div className="mx-3 -mt-6 relative z-10 rounded-xl bg-white/[0.06] border border-white/[0.1] backdrop-blur-xl p-4 mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-[10px] text-emerald-400 font-bold tracking-wider">REGISTERED</span>
                      </div>
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-gray-500">Manufacturer</span>
                          <span className="text-xs font-bold text-primary">DEWALT</span>
                        </div>
                        <div className="h-px bg-white/5" />
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-gray-500">Model</span>
                          <span className="text-xs font-medium text-white">DCD796</span>
                        </div>
                        <div className="h-px bg-white/5" />
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-gray-500">Serial</span>
                          <span className="text-[10px] font-mono text-gray-400">DW-2026-44821</span>
                        </div>
                        <div className="h-px bg-white/5" />
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-gray-500">Value</span>
                          <span className="text-xs font-bold text-emerald-400">£149.99</span>
                        </div>
                        <div className="h-px bg-white/5" />
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-gray-500">TOOLIO ID</span>
                          <span className="text-[10px] font-mono text-primary/60">Kx7mRp2v9bQ</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-primary/10 rounded-lg p-2.5 mt-3">
                        <Shield className="w-3.5 h-3.5 text-primary" />
                        <span className="text-[10px] text-yellow-400 font-bold">Protected by TOOLIO</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Glow effect behind phone */}
                <div className="absolute -inset-1 bg-primary/10 rounded-[3rem] blur-xl -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 py-24 lg:px-12 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold uppercase text-center mb-16">
            Why <span className="text-primary">TOOLIO</span>?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Feature 1 - AI-Powered Scanning */}
            <div className="p-6 border border-primary/50 bg-primary/5 hover:border-primary transition-colors relative overflow-hidden">
              <div className="absolute top-2 right-2 bg-primary/20 text-primary text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI POWERED
              </div>
              <div className="flex items-center gap-3 mb-2">
                <Camera className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase">AI Tool Recognition</h3>
              </div>
              <p className="text-muted-foreground text-base">
                Snap a photo of your tool label. Our AI instantly identifies the manufacturer, model, serial number, tool type and even estimates its value - all in seconds.
              </p>
            </div>

            {/* Feature 2 - Theft Protection */}
            <div className="p-6 border border-border bg-card hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase">Theft Protection</h3>
              </div>
              <p className="text-muted-foreground text-base">
                Mark tools as stolen to prevent re-registration. Get instant notifications when someone scans your stolen tool - including their location.
              </p>
            </div>

            {/* Feature 3 - Police Portal */}
            <div className="p-6 border border-blue-500/30 bg-blue-900/10 hover:border-blue-500 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-8 h-8 md:w-10 md:h-10 text-blue-400 flex-shrink-0" />
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase">Police Portal</h3>
              </div>
              <p className="text-muted-foreground text-base">
                Law enforcement can scan evidence images with AI to instantly identify and verify tools. Access owner contact details for stolen items.
              </p>
            </div>

            {/* Feature 4 - Public Tool Check */}
            <div className="p-6 border border-yellow-500/30 bg-yellow-900/10 hover:border-yellow-500 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <Search className="w-8 h-8 md:w-10 md:h-10 text-yellow-400 flex-shrink-0" />
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase">Public Tool Check</h3>
              </div>
              <p className="text-muted-foreground text-base">
                Buying second-hand? Snap a photo or upload an image - our AI reads the label and checks if it's been reported stolen. No account needed.
              </p>
            </div>

            {/* Feature 5 - Real-time Alerts */}
            <div className="p-6 border border-border bg-card hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <Bell className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase">Real-time Alerts</h3>
              </div>
              <p className="text-muted-foreground text-base">
                Get notified instantly when your stolen tool is scanned - by police, other users, or the public. Know who checked it and where.
              </p>
            </div>

            {/* Feature 6 - Export & Reports */}
            <div className="p-6 border border-border bg-card hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <Download className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase">Export & Reports</h3>
              </div>
              <p className="text-muted-foreground text-base">
                Export your inventory to PDF or CSV. Perfect for insurance claims, audits, or keeping offline records of your tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-24 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold uppercase mb-6">
            Ready to Secure Your Tools?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Join thousands of contractors who trust TOOLIO to manage their power tool inventory.
          </p>
          <Link to="/register">
            <Button size="lg" className="btn-primary text-lg px-12 py-6" data-testid="cta-get-started-btn">
              Create Free Account
            </Button>
          </Link>
          <div className="mt-8">
            <p className="text-muted-foreground">Have questions or need assistance?</p>
            <Link to="/contact" className="text-primary text-lg font-medium hover:underline" data-testid="contact-link">
              Contact Us
            </Link>
            <p className="text-muted-foreground mt-1">we&apos;re here to help 😊</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 lg:px-12 border-t border-border">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-muted-foreground text-sm hover:text-primary transition-colors" data-testid="privacy-link">
              Privacy Policy
            </Link>
            <Link to="/contact" className="text-muted-foreground text-sm hover:text-primary transition-colors">
              Contact
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} TOOLIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
